import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { createContext, useContext, useState, useEffect } from "react";

// Pages
import SplashScreen from "./pages/SplashScreen";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import SetupWallet from "./pages/SetupWallet";
import HomePage from "./pages/HomePage";
import PaymentsPage from "./pages/PaymentsPage";
import SendMoneyPage from "./pages/SendMoneyPage";
import ReceiveMoneyPage from "./pages/ReceiveMoneyPage";
import WithdrawPage from "./pages/WithdrawPage";
import EarnPage from "./pages/EarnPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import CardsPage from "./pages/CardsPage";
import MorePage from "./pages/MorePage";
import TransactionsPage from "./pages/TransactionsPage";
import TransactionDetailPage from "./pages/TransactionDetailPage";
import NotificationsPage from "./pages/NotificationsPage";
import KYCPage from "./pages/KYCPage";
import SettingsPage from "./pages/SettingsPage";
import ClipsPage from "./pages/ClipsPage";
import TippingPage from "./pages/TippingPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import MarketplacePage from "./pages/MarketplacePage";
import DemoGatePage from "./pages/DemoGatePage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminCampaignsPage from "./pages/admin/AdminCampaignsPage";
import AdminPayoutsPage from "./pages/admin/AdminPayoutsPage";
import AdminKYCPage from "./pages/admin/AdminKYCPage";
import AdminTransactionsPage from "./pages/admin/AdminTransactionsPage";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("lekeewallet_token"));
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("lekeewallet_user");
    const storedAdmin = localStorage.getItem("lekeewallet_admin");
    
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (storedAdmin) {
      setAdminUser(JSON.parse(storedAdmin));
      setIsAdmin(true);
    }
    setIsLoading(false);
  }, []);

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem("lekeewallet_token", accessToken);
    localStorage.setItem("lekeewallet_user", JSON.stringify(userData));
  };

  const adminLogin = (adminData, accessToken) => {
    setAdminUser(adminData);
    setIsAdmin(true);
    setToken(accessToken);
    localStorage.setItem("lekeewallet_token", accessToken);
    localStorage.setItem("lekeewallet_admin", JSON.stringify(adminData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAdmin(false);
    setAdminUser(null);
    localStorage.removeItem("lekeewallet_token");
    localStorage.removeItem("lekeewallet_user");
    localStorage.removeItem("lekeewallet_admin");
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem("lekeewallet_user", JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      isLoading, 
      isAdmin, 
      adminUser,
      login, 
      adminLogin,
      logout, 
      updateUser,
      isAuthenticated: !!token && !!user,
      isAdminAuthenticated: !!token && !!adminUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("lekeewallet_theme");
    return saved || "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
    
    localStorage.setItem("lekeewallet_theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ViewMode Context - Controls mobile/desktop view for demo
const ViewModeContext = createContext(null);

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error("useViewMode must be used within ViewModeProvider");
  }
  return context;
};

const ViewModeProvider = ({ children }) => {
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("lekeewallet_viewmode");
    return saved || "auto";
  });

  const isForcedMobile = viewMode === "mobile";
  const isForcedDesktop = viewMode === "desktop";

  useEffect(() => {
    localStorage.setItem("lekeewallet_viewmode", viewMode);
  }, [viewMode]);

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode, isForcedMobile, isForcedDesktop }}>
      {children}
    </ViewModeContext.Provider>
  );
};

// Demo Gate - requires password to access demo
const DemoGate = ({ children }) => {
  const hasAccess = localStorage.getItem('lekeewallet_demo_access') === 'granted';
  if (!hasAccess) return <Navigate to="/" replace />;
  return children;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const hasAccess = localStorage.getItem('lekeewallet_demo_access') === 'granted';
  
  if (!hasAccess) return <Navigate to="/" replace />;
  if (isLoading) return <SplashScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Admin Protected Route
const AdminRoute = ({ children }) => {
  const { isAdminAuthenticated, isLoading } = useAuth();
  const hasAccess = localStorage.getItem('lekeewallet_demo_access') === 'granted';
  
  if (!hasAccess) return <Navigate to="/" replace />;
  if (isLoading) return <SplashScreen />;
  if (!isAdminAuthenticated) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <ViewModeProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-background text-foreground">
              <Routes>
                {/* Demo Gate */}
                <Route path="/" element={<DemoGatePage />} />
                <Route path="/splash" element={<DemoGate><SplashScreen /></DemoGate>} />
                
                {/* Public Routes (behind demo gate) */}
                <Route path="/login" element={<DemoGate><LoginPage /></DemoGate>} />
                <Route path="/register" element={<DemoGate><RegisterPage /></DemoGate>} />
                <Route path="/setup" element={<SetupWallet />} />
                
                {/* Protected User Routes */}
                <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                <Route path="/payments" element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
                <Route path="/send" element={<ProtectedRoute><SendMoneyPage /></ProtectedRoute>} />
                <Route path="/receive" element={<ProtectedRoute><ReceiveMoneyPage /></ProtectedRoute>} />
                <Route path="/withdraw" element={<ProtectedRoute><WithdrawPage /></ProtectedRoute>} />
                <Route path="/earn" element={<ProtectedRoute><EarnPage /></ProtectedRoute>} />
                <Route path="/campaigns/:id" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
                <Route path="/cards" element={<ProtectedRoute><CardsPage /></ProtectedRoute>} />
                <Route path="/more" element={<ProtectedRoute><MorePage /></ProtectedRoute>} />
                <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
                <Route path="/transactions/:id" element={<ProtectedRoute><TransactionDetailPage /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                <Route path="/kyc" element={<ProtectedRoute><KYCPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/clips" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />
                <Route path="/tipping" element={<ProtectedRoute><TippingPage /></ProtectedRoute>} />
                <Route path="/subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
                <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
                <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
                <Route path="/admin/campaigns" element={<AdminRoute><AdminCampaignsPage /></AdminRoute>} />
                <Route path="/admin/payouts" element={<AdminRoute><AdminPayoutsPage /></AdminRoute>} />
                <Route path="/admin/kyc" element={<AdminRoute><AdminKYCPage /></AdminRoute>} />
                <Route path="/admin/transactions" element={<AdminRoute><AdminTransactionsPage /></AdminRoute>} />
                
                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster 
                position="top-center" 
                toastOptions={{
                  style: {
                    background: 'hsl(var(--card))',
                    color: 'hsl(var(--foreground))',
                    border: '1px solid hsl(var(--border))',
                  },
                }}
              />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ViewModeProvider>
    </ThemeProvider>
  );
}

export default App;
